import { NodeViewRendererProps } from '@tiptap/react';
import { Mention } from '@tiptap/extension-mention';

const CustomHashtag = Mention.extend({
  name: 'hashtag',
  
  addAttributes() {
    return {
      label: {},
      issueData: {
        default: null,
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    // This defines how the node is serialized to HTML (stored in your database)
    const isIssueTag = node.attrs.label && node.attrs.label.startsWith('issue_tag_');
    
    if (isIssueTag) {
      // Get issue data or extract from label
      let issueData = node.attrs.issueData;
      if (!issueData && node.attrs.label) {
        const cname = node.attrs.label.replace('issue_tag_', '');
        issueData = {
          cname: cname,
          name: cname.replace(/_/g, ' '),
          type: 'issue'
        };
      }
      
      const attributes: Record<string, any> = {
        ...HTMLAttributes,
        'data-type': 'hashtag',
        'data-label': node.attrs.label,
        class: 'hashtag issue-tag',
      };
      
      // Add issue data attributes if available
      if (issueData) {
        if (issueData.id) attributes['data-issue-id'] = String(issueData.id);
        if (issueData.name) attributes['data-issue-name'] = issueData.name;
        if (issueData.cname) attributes['data-issue-cname'] = issueData.cname;
      }
      
      return [
        'span',
        attributes,
        `#${issueData ? issueData.name : node.attrs.label.replace('issue_tag_', '').replace(/_/g, ' ')}`,
      ];
    }

    return [
      'span',
      {
        ...HTMLAttributes,
        'data-type': 'hashtag',
        'data-label': node.attrs.label,
        class: 'hashtag',
      },
      `#${node.attrs.label}`,
    ];
  },

  addNodeView() {
    // This defines how the node is rendered in the editor
    return ({ node }: NodeViewRendererProps) => {
      const isIssueTag = node.attrs.label && node.attrs.label.startsWith('issue_tag_');
      
      if (isIssueTag) {
        // If we have issueData, use it. Otherwise, try to extract from label
        let issueData = node.attrs.issueData;
        if (!issueData && node.attrs.label) {
          // Extract cname from issue_tag_ prefix
          const cname = node.attrs.label.replace('issue_tag_', '');
          issueData = {
            cname: cname,
            name: cname.replace(/_/g, ' '), // Convert underscores to spaces
            type: 'issue'
          };
        }
        
        if (issueData) {
          const container = document.createElement('a');
          container.setAttribute('href', `/issue/${issueData.cname}`);
          container.setAttribute('class', 'hashtag issue-tag inline-flex items-center');
          container.style.textDecoration = 'none';
          
          // Create the '#' symbol with background
          const hashSymbol = document.createElement('span');
          hashSymbol.setAttribute('class', 'inline-flex items-center justify-center size-4 rounded-full text-xs font-medium bg-soc-purple text-white mr-0.5');
          hashSymbol.textContent = '#';
          
          // Create the issue name with bold (no underline)
          const issueName = document.createElement('span');
          issueName.setAttribute('class', 'font-bold text-eoe-text dark:text-eoe-dark-text');
          issueName.textContent = issueData.name;
          
          container.appendChild(hashSymbol);
          container.appendChild(issueName);
          
          return {
            dom: container,
          };
        }
      }
      
      // Fallback for regular hashtags or when issue data is missing
      const dom = document.createElement('a');
      const isIssueTagFallback = node.attrs.label && node.attrs.label.startsWith('issue_tag_');
      
      if (isIssueTagFallback) {
        // For issue tags without proper data, still show as issue format
        const cname = node.attrs.label.replace('issue_tag_', '');
        const issueName = cname.replace(/_/g, ' ');
        
        dom.setAttribute('href', `/issue/${cname}`);
        dom.setAttribute('class', 'hashtag issue-tag inline-flex items-center');
        dom.style.textDecoration = 'none';
        
        const hashSymbol = document.createElement('span');
        hashSymbol.setAttribute('class', 'inline-flex items-center justify-center size-4 rounded-full text-xs font-medium bg-soc-purple text-white mr-0.5');
        hashSymbol.textContent = '#';
        
        const issueNameSpan = document.createElement('span');
        issueNameSpan.setAttribute('class', 'font-bold text-eoe-text dark:text-eoe-dark-text');
        issueNameSpan.textContent = issueName;
        
        dom.appendChild(hashSymbol);
        dom.appendChild(issueNameSpan);
      } else {
        dom.setAttribute('href', `/tag/${node.attrs.label}`);
        dom.setAttribute('class', 'hashtag inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-eoe-entity-tag text-white mr-1');
        dom.textContent = `#${node.attrs.label}`;
      }
      
      return {
        dom,
      };
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="hashtag"]',
        getAttrs: (dom) => {
          const element = dom as HTMLElement;
          const label = element.getAttribute('data-label');
          const issueId = element.getAttribute('data-issue-id');
          const issueName = element.getAttribute('data-issue-name');
          const issueCname = element.getAttribute('data-issue-cname');
          
          const attrs: any = { label };
          
          // Check if this is an issue tag
          if (label && label.startsWith('issue_tag_')) {
            if (issueId && issueName && issueCname) {
              // Use stored data attributes
              attrs.issueData = {
                id: parseInt(issueId, 10),
                name: issueName,
                cname: issueCname,
                type: 'issue'
              };
            } else {
              // Fallback: extract from label
              const cname = label.replace('issue_tag_', '');
              attrs.issueData = {
                cname: cname,
                name: cname.replace(/_/g, ' '),
                type: 'issue'
              };
            }
          }
          
          return attrs;
        },
      },
    ];
  },
});

export default CustomHashtag;